import React, { useState, useEffect } from 'react';
import { Link, Outlet } from 'react-router-dom';
import { Menu, X, LogOut, User, MessageCircle, Instagram } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getPublishedPages, PageMeta } from '../utils/storage';

const Layout: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [publishedPages, setPublishedPages] = useState<PageMeta[]>([]);
  const { user, signOut } = useAuth();

  useEffect(() => {
    const loadPublishedPages = async () => {
      const pages = await getPublishedPages();
      setPublishedPages(pages);
    };
    loadPublishedPages();
  }, []);

  const isPageVisible = (slug: string): boolean => {
    // Show if user is logged in OR if page is published
    if (user) return true;
    return publishedPages.some(p => p.slug === slug);
  };

  const handleSignOut = async () => {
    await signOut();
    setIsMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white shadow-lg">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-2">
              <img src="/TF_Graphic.png" alt="TrabajoFuera" className="h-12 w-auto" />
              <div className="flex flex-col text-left">
                <span className="text-xl font-bold text-gray-800 leading-none">Trabajo</span>
                <span className="text-xl font-bold text-gray-800 leading-none">Fuera</span>
              </div>
            </Link>
            
            <div className="hidden md:flex items-center space-x-8">
              <Link to="/" className="text-gray-700 hover:text-red-600 transition">Inicio</Link>
              <Link to="/jobs" className="text-gray-700 hover:text-red-600 transition">Ofertas de Trabajo</Link>
              {isPageVisible('eures') && (
                <Link to="/eures" className="text-gray-700 hover:text-red-600 transition">EURES</Link>
              )}
              <Link to="/contact" className="text-gray-700 hover:text-red-600 transition">Contacto</Link>

              <div className="flex items-center space-x-3">
                <a
                  href="https://www.instagram.com/trabajofuera"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-pink-600 hover:text-pink-700 transition"
                  aria-label="Instagram"
                >
                  <Instagram className="w-5 h-5" />
                </a>
                <a
                  href="https://www.tiktok.com/@trabajofuera"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-800 hover:text-gray-900 transition"
                  aria-label="TikTok"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                  </svg>
                </a>
                <a
                  href="https://wa.me/34678601100"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-full flex items-center space-x-2 transition shadow-md hover:shadow-lg"
                  aria-label="Contact via WhatsApp"
                >
                  <MessageCircle className="w-5 h-5" />
                  <span className="font-semibold text-sm">Contacta</span>
                </a>
              </div>

              {user && (
                <>
                  <Link to="/admin" className="text-gray-700 hover:text-red-600 transition">Gestión</Link>
                  <div className="flex items-center space-x-4">
                    <span className="flex items-center text-gray-700">
                      <User className="w-4 h-4 mr-1" />
                      {user.email}
                    </span>
                    <button
                      onClick={handleSignOut}
                      className="flex items-center text-gray-700 hover:text-red-600 transition"
                    >
                      <LogOut className="w-4 h-4 mr-1" />
                      Cerrar Sesión
                    </button>
                  </div>
                </>
              )}
            </div>

            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2"
            >
              {isMenuOpen ? <X className="w-8 h-8" /> : <Menu className="w-8 h-8" />}
            </button>
          </div>

          {isMenuOpen && (
            <div className="md:hidden pb-4">
              <Link to="/" className="block py-2 text-gray-700 hover:text-red-600" onClick={() => setIsMenuOpen(false)}>
                Inicio
              </Link>
              <Link to="/jobs" className="block py-2 text-gray-700 hover:text-red-600" onClick={() => setIsMenuOpen(false)}>
                Ofertas de Trabajo
              </Link>
              {isPageVisible('eures') && (
                <Link to="/eures" className="block py-2 text-gray-700 hover:text-red-600" onClick={() => setIsMenuOpen(false)}>
                  EURES
                </Link>
              )}
              <Link to="/contact" className="block py-2 text-gray-700 hover:text-red-600" onClick={() => setIsMenuOpen(false)}>
                Contacto
              </Link>

              <div className="py-4 border-t border-gray-200 mt-2">
                <p className="text-sm font-semibold text-gray-600 mb-3">Síguenos</p>
                <div className="flex items-center space-x-3">
                  <a
                    href="https://www.instagram.com/trabajofuera"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-pink-600 hover:text-pink-700 transition"
                    aria-label="Instagram"
                  >
                    <Instagram className="w-6 h-6" />
                  </a>
                  <a
                    href="https://www.tiktok.com/@trabajofuera"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-800 hover:text-gray-900 transition"
                    aria-label="TikTok"
                  >
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                    </svg>
                  </a>
                  <a
                    href="https://wa.me/34678601100"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-full flex items-center space-x-2 transition shadow-md"
                    aria-label="Contact via WhatsApp"
                  >
                    <MessageCircle className="w-5 h-5" />
                    <span className="font-semibold text-sm">Contacta</span>
                  </a>
                </div>
              </div>

              {user && (
                <>
                  <Link to="/admin" className="block py-2 text-gray-700 hover:text-red-600" onClick={() => setIsMenuOpen(false)}>
                    Gestión
                  </Link>
                  <div className="py-2 text-gray-700 flex items-center">
                    <User className="w-4 h-4 mr-1" />
                    {user.email}
                  </div>
                  <button
                    onClick={handleSignOut}
                    className="block py-2 text-gray-700 hover:text-red-600 flex items-center"
                  >
                    <LogOut className="w-4 h-4 mr-1" />
                    Cerrar Sesión
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </nav>

      <main>
        <Outlet />
      </main>

      <footer className="bg-gray-800 text-white py-8">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <h3 className="font-bold mb-4">TrabajoFuera</h3>
              <p className="text-gray-300">Tu puerta de entrada al trabajo internacional</p>
            </div>
            <div>
              <h3 className="font-bold mb-4">Enlaces Rápidos</h3>
              <ul className="space-y-2 text-gray-300">
                <li><Link to="/jobs" className="hover:text-white">Ofertas de Trabajo</Link></li>
                {isPageVisible('eures') && (
                  <li><Link to="/eures" className="hover:text-white">Programa EURES</Link></li>
                )}
                <li><Link to="/contact" className="hover:text-white">Contacto</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold mb-4">Contacto</h3>
              <p className="text-gray-300">
                <a href="mailto:info@trabajofuera.com" className="hover:text-white transition">
                  info@trabajofuera.com
                </a>
              </p>
              <p className="text-gray-300">
                <a href="tel:+34678601100" className="hover:text-white transition">
                  +34 678 601 100
                </a>
              </p>
              <div className="mt-4">
                <h4 className="font-semibold mb-3">Síguenos</h4>
                <div className="flex space-x-4">
                  <a
                    href="https://wa.me/34678601100"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-green-500 hover:bg-green-600 text-white p-2 rounded-full transition"
                    aria-label="WhatsApp"
                  >
                    <MessageCircle className="w-5 h-5" />
                  </a>
                  <a
                    href="https://www.instagram.com/trabajofuera"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-gradient-to-br from-purple-500 via-pink-500 to-orange-500 hover:opacity-90 text-white p-2 rounded-full transition"
                    aria-label="Instagram"
                  >
                    <Instagram className="w-5 h-5" />
                  </a>
                  <a
                    href="https://www.tiktok.com/@trabajofuera"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-black hover:bg-gray-800 text-white p-2 rounded-full transition"
                    aria-label="TikTok"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-700 mt-8 pt-8">
            <div className="flex flex-wrap justify-center gap-4 mb-4 text-sm">
              <Link to="/aviso-legal" className="text-gray-400 hover:text-white transition">Aviso Legal</Link>
              <span className="text-gray-600">|</span>
              <Link to="/politica-de-privacidad" className="text-gray-400 hover:text-white transition">Política de Privacidad</Link>
              <span className="text-gray-600">|</span>
              <Link to="/terminos-y-condiciones" className="text-gray-400 hover:text-white transition">Términos y Condiciones</Link>
              <span className="text-gray-600">|</span>
              <Link to="/politica-de-cookies" className="text-gray-400 hover:text-white transition">Política de Cookies</Link>
            </div>
            <p className="text-center text-gray-400">&copy; {new Date().getFullYear()} TrabajoFuera. Todos los derechos reservados.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;